// File: lib/config.ts

// ===== COMMISSION RATES =====
// Service fee configuration - Fixed + Percentage model for buyers
export const SERVICE_FEE_FIXED = Number(
  process.env.NEXT_PUBLIC_SERVICE_FEE_FIXED ?? "1.25"
);

export const SERVICE_FEE_RATE = Number(
  process.env.NEXT_PUBLIC_SERVICE_FEE_RATE ?? "0.05"
);

// Legacy service fee rate (percentage only) - kept for backward compatibility
export const SERVICE_FEE_RATE_LEGACY = Number(
  process.env.NEXT_PUBLIC_SERVICE_FEE_RATE_LEGACY ?? "0.05"
);

// Selling fee applied to sellers (deducted from their earnings) - UNCHANGED
export const SELLING_FEE_RATE = Number(
  process.env.NEXT_PUBLIC_SELLING_FEE_RATE ?? "0.03"
);

/**
 * Calculate service fee using Fixed + Percentage model for buyers
 * @param amount The subtotal + shipping amount in USD
 * @returns The calculated service fee that buyers pay
 */
export function calculateServiceFee(amount: number): number {
  // Si le montant est 0 (panier vide), pas de frais de service
  if (amount === 0) return 0;
  
  // Use new fixed + percentage model if fixed amount is configured
  if (SERVICE_FEE_FIXED > 0) {
    return SERVICE_FEE_FIXED + (amount * SERVICE_FEE_RATE);
  }
  
  // Fallback to legacy percentage-only model
  return amount * SERVICE_FEE_RATE_LEGACY;
}

/**
 * Get the service fee description for display purposes
 * @returns A human-readable description of the fee structure
 */
export function getServiceFeeDescription(): string {
  if (SERVICE_FEE_FIXED > 0) {
    return `$${SERVICE_FEE_FIXED.toFixed(2)} + ${(SERVICE_FEE_RATE * 100).toFixed(1)}%`;
  }
  
  return `${(SERVICE_FEE_RATE_LEGACY * 100).toFixed(1)}%`;
}

// ===== SHIPPING (Shippo white-label) =====
/**
 * Safety buffer added on top of the live Shippo carrier rate when charging the
 * buyer, as a fraction (0.05 = +5%). Default 0 → buyer pays the exact rate.
 * Lever to absorb small carrier DIM/weight adjustments at the MVP (Kraahl does
 * not yet bill sellers for adjustments — that is the V2 wallet/ledger).
 */
export const SHIPPING_SAFETY_BUFFER_RATE = Number(
  process.env.SHIPPING_SAFETY_BUFFER_RATE ?? "0"
);

/** Apply the configured safety buffer to a raw carrier rate amount (USD). */
export function applyShippingBuffer(rateAmount: number): number {
  if (!(SHIPPING_SAFETY_BUFFER_RATE > 0)) return rateAmount;
  return rateAmount * (1 + SHIPPING_SAFETY_BUFFER_RATE);
}

/**
 * Tiered buffer (§I.1, locked 2026-06-16 in §L.6 Q3):
 *   buffered = max(rate + FLAT_FLOOR_USD, rate * (1 + PERCENT_RATE))
 *
 * Defaults locked at $0.50 + 5%. Both env-overridable.
 *
 * Why tiered: 5% of a $5 rate = $0.25, smaller than common carrier rounding
 * ($0.50); flat floor covers small parcels, percent covers large ones.
 *
 * Lives next to (NOT replaces) `applyShippingBuffer` — that helper is wired
 * into the capture-path resolver (`lib/payments/shippo-rate-resolve.ts`) and
 * already covers a different envelope (rate re-fetch by `rateId`). WS4.1
 * rates-endpoint uses this tiered version per the LOCKED §I.1 default.
 */
export const SHIPPING_FLAT_FLOOR_USD = Number(
  process.env.SHIPPING_FLAT_FLOOR_USD ?? "0.50"
);
export const SHIPPING_PERCENT_RATE = Number(
  process.env.SHIPPING_PERCENT_RATE ?? "0.05"
);

export function applyTieredShippingBuffer(rateAmount: number): number {
  if (!Number.isFinite(rateAmount) || rateAmount < 0) return rateAmount;
  const withFloor = rateAmount + SHIPPING_FLAT_FLOOR_USD;
  const withPercent = rateAmount * (1 + SHIPPING_PERCENT_RATE);
  return Math.max(withFloor, withPercent);
}


// ===== PAYPAL CONFIGURATION =====
// URLs de base PayPal selon l'environnement.
//
// Env-var wins over the NODE_ENV fallback. This matters for `next build +
// next start` paths that must run against SANDBOX (E2E gate on the staging
// VPS): `next start` forces NODE_ENV=production, but the process env still
// carries PAYPAL_BASE_URL=https://api-m.sandbox.paypal.com from .env.local.
// Without this override, sandbox creds get sent to the live API → 401
// invalid_client. `make dev`/`make stg`/`make prod` all use `next dev`
// (NODE_ENV=development) so they were unaffected — the bug only shows up
// when a prod-mode build is combined with sandbox creds.
export const PAYPAL_BASE_URL =
  process.env.PAYPAL_BASE_URL ??
  (process.env.NODE_ENV === 'production'
    ? "https://api-m.paypal.com"      // Production PayPal API
    : "https://api-m.sandbox.paypal.com"); // Sandbox PayPal API

// Client-safe environment label (single SoT for sandbox vs live on BOTH sides
// of the bundle boundary). `PAYPAL_BASE_URL` above resolves correctly on the
// SERVER but webpack does not inline non-`NEXT_PUBLIC_*` env vars into the
// client bundle — on the client `process.env.PAYPAL_BASE_URL` is undefined,
// so a `next start`-based flow (E2E gate) falls through to the
// NODE_ENV=production default and points the v6 SDK bundle at LIVE paypal.com
// even when the server is sandbox. Solved by lifting the env/live decision
// into a NEXT_PUBLIC_ label that webpack DOES inline at build time.
//
// Resolution order:
//   1. `NEXT_PUBLIC_PAYPAL_ENVIRONMENT` (client + server, inlined at build)
//   2. Fallback: `PAYPAL_BASE_URL.includes('sandbox')` (server-only signal)
//
// Prod (Vercel): leave `NEXT_PUBLIC_PAYPAL_ENVIRONMENT` unset → client falls
// through to NODE_ENV=production default of `PAYPAL_BASE_URL` (live) → label
// = "live". Behaviour unchanged.
//
// @see .tondocs/infra/HANDOFF_E2E_2026_07_07.md §6.1
const rawPaypalEnvLabel = process.env.NEXT_PUBLIC_PAYPAL_ENVIRONMENT;
export const PAYPAL_ENVIRONMENT_LABEL: "sandbox" | "live" =
  rawPaypalEnvLabel === "sandbox" || rawPaypalEnvLabel === "live"
    ? rawPaypalEnvLabel
    : PAYPAL_BASE_URL.includes("sandbox")
      ? "sandbox"
      : "live";

export const IS_PAYPAL_SANDBOX = PAYPAL_ENVIRONMENT_LABEL === "sandbox";

// ===== E2E TEST-MODE HOOKS =====
/**
 * PayPal sandbox reCAPTCHA Enterprise blocks the buyer-login popup in
 * Playwright CI (`.tondocs/infra/PAYPAL_CAPTCHA_BLOCKER.md` §2/§5, variant
 * A). When this flag is on, the PayPal button click handler in
 * `components/cart/paypal-checkout.tsx` skips `session.start()` — the call
 * that opens the blocked popup — and fires `onApprove` directly with the
 * orderId from a REAL `POST /api/paypal-orders/create` (server-to-server
 * sandbox REST, no browser/popup involved, not captcha-gated). The capture
 * leg is then mocked at the Playwright network layer; see
 * `tests/e2e/paypal-checkout-smoke.spec.ts` for what that mocked skeleton
 * does and does not prove.
 *
 * `NEXT_PUBLIC_*` → inlined client + server at build time. Gated with
 * `IS_PAYPAL_SANDBOX` as defence-in-depth: a stray/misconfigured env var can
 * never activate this against live PayPal. Unset in every `.env*` outside
 * the `e2e-gate` CI job's env block — `false` (dead branch) in every
 * prod/staging bundle.
 */
export const E2E_PAYPAL_MOCK_ENABLED =
  process.env.NEXT_PUBLIC_E2E_PAYPAL_MOCK === "1" && IS_PAYPAL_SANDBOX;

// PayPal Demo Mode (merchant status mocking — legacy, used by paypal-debug.ts)
export const PAYPAL_DEMO_MODE = process.env.PAYPAL_DEMO_MODE === 'true';

// PayPal Negative Testing (sandbox only — mock error responses from PayPal API)
export const PAYPAL_NEGATIVE_TESTING = process.env.PAYPAL_NEGATIVE_TESTING === 'true';
export const PAYPAL_NEGATIVE_TESTING_CLIENT = process.env.NEXT_PUBLIC_PAYPAL_NEGATIVE_TESTING === 'true';

/**
 * Validates that PayPal environment configuration is safe.
 * Prevents accidentally using production PayPal in development mode.
 *
 * @throws Error if NODE_ENV=development but PAYPAL_BASE_URL is production
 * @returns { isSandbox: boolean, nodeEnv: string } for logging purposes
 */
export function validatePayPalEnvironment(): { isSandbox: boolean; nodeEnv: string } {
  const nodeEnv = process.env.NODE_ENV || 'development';
  const isSandbox = IS_PAYPAL_SANDBOX;

  // SAFETY CHECK: Block production PayPal in development mode
  if (nodeEnv === 'development' && !isSandbox) {
    throw new Error(
      '🚨 PAYPAL SAFETY ERROR: Attempting to use PRODUCTION PayPal in DEVELOPMENT mode!\n' +
      `NODE_ENV: ${nodeEnv}\n` +
      `PAYPAL_BASE_URL: ${PAYPAL_BASE_URL}\n` +
      'To fix: Set PAYPAL_BASE_URL=https://api-m.sandbox.paypal.com in your .env file'
    );
  }

  return { isSandbox, nodeEnv };
}

// ===== DOMAIN CONFIGURATION =====
// Domaine principal selon l'environnement
export const BASE_DOMAIN = process.env.NEXT_PUBLIC_BASE_DOMAIN ?? 
  (process.env.VERCEL_URL 
    ? `https://${process.env.VERCEL_URL}` 
    : process.env.NODE_ENV === 'production' 
      ? "https://kraahl.com"
      : "http://localhost:3000");

// Email de l'organisation
export const ORGANIZATION_EMAIL = process.env.NEXT_PUBLIC_ORGANIZATION_EMAIL ?? "no-reply@kraahl.com";

// Email de contact principal
export const CONTACT_EMAIL = process.env.NEXT_PUBLIC_CONTACT_EMAIL ?? "contact@kraahl.com";

// Email vendeur par défaut pour les tests PayPal
export const DEFAULT_SELLER_EMAIL = process.env.PAYPAL_DEFAULT_SELLER_EMAIL ?? "payouts@kraahl.com";

// ===== TIMEOUTS & DELAYS =====
// Délai de validation PayPal (millisecondes) - défaut 24h
export const PAYPAL_VALIDATION_INTERVAL = Number(
  process.env.PAYPAL_VALIDATION_INTERVAL_MS ?? "86400000"
);

// Délai d'expiration des réservations panier (millisecondes) - défaut 5 min
export const CART_RESERVATION_TIMEOUT = Number(
  process.env.CART_RESERVATION_TIMEOUT_MS ?? "300000"
);

/** Checkout countdown (seconds). Set NEXT_PUBLIC_CHECKOUT_COUNTDOWN_SECONDS (e.g. 900 = 15 min). Default 300. */
export const CHECKOUT_COUNTDOWN_SECONDS = Number(
  process.env.NEXT_PUBLIC_CHECKOUT_COUNTDOWN_SECONDS ?? process.env.CHECKOUT_COUNTDOWN_SECONDS ?? "300"
);

// ===== POPUP CONFIGURATION =====
// Dimensions popup PayPal
export const PAYPAL_POPUP_WIDTH = Number(
  process.env.PAYPAL_POPUP_WIDTH ?? "600"
);

export const PAYPAL_POPUP_HEIGHT = Number(
  process.env.PAYPAL_POPUP_HEIGHT ?? "700"
);

// ===== EXTERNAL ASSETS =====
// URL pour les flags de pays
export const FLAGS_API_URL = process.env.FLAGS_API_URL ?? "https://flagcdn.com/w40";

// URL placeholder avatar par défaut (optionnel - fallback sur icônes si non défini)
export const DEFAULT_AVATAR_URL = process.env.NEXT_PUBLIC_DEFAULT_AVATAR_URL || undefined;

// ===== CONTEST / CAMPAIGN CONFIG =====
// Public name of the current contest (shown on page)
export const CONTEST_NAME = process.env.NEXT_PUBLIC_CONTEST_NAME || "Contest";
// ISO timestamp (UTC) for contest start (deployment date recommended)
export const CONTEST_START_ISO = process.env.NEXT_PUBLIC_CONTEST_START_ISO || "2025-08-20T00:00:00Z";
// Duration in days (used if END not explicitly provided)
export const CONTEST_DURATION_DAYS = Number(process.env.NEXT_PUBLIC_CONTEST_DURATION_DAYS || "30");
// Explicit end overrides computed end (optional)
export const CONTEST_END_ISO = process.env.NEXT_PUBLIC_CONTEST_END_ISO || "";
// Toggle contest visibility
export const CONTEST_ENABLED = (process.env.NEXT_PUBLIC_CONTEST_ENABLED ?? "true").toLowerCase() === "true";
// Separate banner toggle (allows hiding banner without disabling contest logic)

export function getContestStartDate(): Date { return new Date(CONTEST_START_ISO); }
export function getContestEndDate(): Date {
  if (CONTEST_END_ISO) return new Date(CONTEST_END_ISO);
  const start = getContestStartDate();
  const end = new Date(start.getTime() + CONTEST_DURATION_DAYS * 24 * 60 * 60 * 1000);
  return end;
}

export function getContestState(): "disabled" | "upcoming" | "active" | "ended" {
  if (!CONTEST_ENABLED) return "disabled";
  const now = Date.now();
  const start = getContestStartDate().getTime();
  const end = getContestEndDate().getTime();
  if (now < start) return "upcoming";
  if (now >= end) return "ended";
  return "active";
}

export const CHAT_SELL_ITEM_ENABLED = (process.env.NEXT_PUBLIC_CHAT_SELL_ITEM_ENABLED ?? "false").toLowerCase() === "true";

/** Master switch for contact/off-platform moderation (heuristics + OpenAI). Default off. */
export const CONTACT_MODERATION_ENABLED =
  (process.env.CONTACT_MODERATION_ENABLED ?? "false").toLowerCase() === "true";

export const MAINTENANCE_MODE = process.env.MAINTENANCE_MODE?.toLowerCase() === "true";

export const RECOMMENDATIONS_V1_ENABLED =
  (process.env.NEXT_PUBLIC_RECOMMENDATIONS_V1_ENABLED ?? process.env.RECOMMENDATIONS_V1_ENABLED ?? "false").toLowerCase() === "true";

// ===== PAYPAL v6 SDK =====
// v6 core script URL — routed by IS_PAYPAL_SANDBOX, which now derives from
// PAYPAL_ENVIRONMENT_LABEL. On the client that label is inlined from
// NEXT_PUBLIC_PAYPAL_ENVIRONMENT at build time, so a `next start` build
// against sandbox creds (E2E gate) loads the sandbox SDK bundle in the
// browser, not the live one.
export const PAYPAL_V6_SCRIPT_URL = IS_PAYPAL_SANDBOX
  ? 'https://www.sandbox.paypal.com/web-sdk/v6/core'
  : 'https://www.paypal.com/web-sdk/v6/core';

// ===== DIGITAL WALLETS (v6 SDK) =====
export const APPLE_PAY_ENABLED = (process.env.NEXT_PUBLIC_APPLE_PAY_ENABLED ?? "false") === "true";
export const GOOGLE_PAY_ENABLED = (process.env.NEXT_PUBLIC_GOOGLE_PAY_ENABLED ?? "false") === "true";

/**
 * Returns the list of v6 SDK components to load based on feature flags.
 * "paypal-payments" is always included (covers PayPal, Venmo, Pay Later).
 * Apple Pay and Google Pay are added only when their flags are enabled.
 */
export function getPayPalV6Components(): string[] {
  const components = ["paypal-payments", "paypal-guest-payments"];
  if (APPLE_PAY_ENABLED) components.push("applepay-payments");
  if (GOOGLE_PAY_ENABLED) components.push("googlepay-payments");
  return components;
}

// External SDK URLs for digital wallets
export const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
export const GOOGLE_PAY_SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';

// ===== PAYPAL DROP-RESILIENCE (T4) =====
//
// Per-seller rate threshold for the drop-guard cron / Axiom monitor. If MORE
// than {@link PAYPAL_DROP_GUARD_THRESHOLD} `SELLER_PAYPAL_RESTRICTED` events
// fire on the same `seller_id` within {@link PAYPAL_DROP_GUARD_WINDOW_MS}, the
// guard raises a P1 alert AND auto-pauses the affected listing(s) until the
// seller verifies (PayPal webhook from MERCHANT.PARTNER-CONSENT.REVOKED /
// admin override clears the sentinel).
//
// Tuning rationale (drop incident 2026-06-26, listing 9c28998a-…):
//   - 30+ events in 12 min observed → 5 in 60s is ~3× over-conservative,
//     comfortably catches the symptom while avoiding alert-fatigue on a
//     one-off PAYEE_NOT_VERIFIED hit.
//   - Window is short on purpose: a slow trickle of buyer-side declines on
//     a tier-1 seller is a different signal (their CCP coverage gap, etc.)
//     and should not auto-pause the listing.
//
// Constants are CENTRALISED here so the cron, the Axiom monitor definition,
// and any future dashboard query read the same source of truth (T4
// acceptance).
//
// @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T4
// @see app/api/cron/paypal-drop-guard/route.ts
export const PAYPAL_DROP_GUARD_THRESHOLD = 5;
export const PAYPAL_DROP_GUARD_WINDOW_MS = 60_000;
